import React, { useCallback, useEffect, useState } from 'react';
import baseURL from '../url';
import './ProductsPage.css';
import ProductosLoading from '../ProductosLoading/ProductosLoading';
import { Link as Anchor, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faShoppingCart } from '@fortawesome/free-solid-svg-icons';
import Cart from '../Cart/Cart';
import { getProductMoneda } from '../moneda';
import { getItemOptions, isCustomPrice, isStockNotRequired, getStoredCartItems, serializeCartItems } from '../productStock';
import { guardarSiteConfig, obtenerSiteConfigGuardada } from '../siteConfig';
import ProductMedia, { getFirstProductMedia } from '../ProductMedia/ProductMedia';

function formatOutOfStockLabel(value) {
    return value === 'vendido' ? 'Vendido' : 'Agotado';
}

function calcularPrecioMaximo(productos) {
    return Math.max(0, ...productos.map(producto => Number(producto.precio) || 0));
}

export default function ProductsPage() {
    const navigate = useNavigate();
    const [productos, setProductos] = useState([]);
    const [categorias, setCategorias] = useState([]);
    const [loading, setLoading] = useState(true);
    const [categoriasSeleccionadas, setCategoriasSeleccionadas] = useState('');
    const [marcaSeleccionada, setMarcaSeleccionada] = useState('');
    const [searchText, setSearchText] = useState('');
    const [precioMinimo, setPrecioMinimo] = useState(0);
    const [precioMaximo, setPrecioMaximo] = useState(0);
    const [precioActual, setPrecioActual] = useState(0);
    const [outOfStockLabel, setOutOfStockLabel] = useState(() => formatOutOfStockLabel(obtenerSiteConfigGuardada()?.outOfStockLabel));

    const cargarProductos = useCallback(() => {
        fetch(`${baseURL}/productosGet.php`, {
            method: 'GET',
        })
            .then(response => response.json())
            .then(data => {
                console.log('Productos cargados:', data.productos); // Debug
                const nextProducts = Array.isArray(data.productos) ? data.productos : [];
                setProductos(nextProducts);
                const maxPrecio = calcularPrecioMaximo(nextProducts);
                setPrecioMaximo(maxPrecio);
                setPrecioActual(maxPrecio);
                setLoading(false);
            })
            .catch(error => console.error('Error al cargar productos:', error));
    }, []);

    const cargarConfiguracion = useCallback(() => {
        fetch(`${baseURL}/configGet.php?ts=${Date.now()}`, {
            method: 'GET',
            cache: 'no-store',
        })
            .then(response => response.json())
            .then(data => {
                if (data.config) {
                    guardarSiteConfig(data.config);
                    setOutOfStockLabel(formatOutOfStockLabel(data.config.outOfStockLabel));
                }
            })
            .catch(error => console.error('Error al cargar configuración de productos:', error));
    }, []);
 
    useEffect(() => {
        cargarProductos();
        cargarCategorias();
        cargarConfiguracion();
    }, [cargarConfiguracion, cargarProductos]);

    const cargarCategorias = () => {
        fetch(`${baseURL}/categoriasGet.php`, {
            method: 'GET',
        })
            .then(response => response.json())
            .then(data => {
                console.log('Categorías cargadas:', data.categorias); // Debug
                setCategorias(data.categorias || []);
            })
            .catch(error => console.error('Error al cargar categorías:', error));
    };

    const obtenerImagen = (item) => getFirstProductMedia(item);

    const getCategoryLabel = (idCategoria) =>
        categorias.find((categoria) => categoria.idCategoria === idCategoria)?.categoria || '';

    const getProductPath = (item) => `/producto/${item.idProducto}/${item.titulo.replace(/\s+/g, '-')}`;

    const getAvailableStock = (item) => {
        if (isStockNotRequired(item)) {
            return Number.POSITIVE_INFINITY;
        }

        const itemOptions = getItemOptions(item || {});
        if (itemOptions.length > 0) {
            return itemOptions.reduce((total, option) => total + Math.max(0, Number(option?.stock) || 0), 0);
        }

        return Math.max(0, Number(item?.stock) || 0);
    };

    const renderPrice = (item) => (
        isCustomPrice(item) ? (
            <h5>Personalizable</h5>
        ) : (
            <>
                <h5> {getProductMoneda(item)} {String(item?.precio)?.replace(/\B(?=(\d{3})+(?!\d))/g, ".")}</h5>
                {item.precioAnterior !== 0 && item.precioAnterior !== undefined && (
                    <h5 className='precioTachado'>{getProductMoneda(item)} {`${item?.precioAnterior}`.replace(/\B(?=(\d{3})+(?!\d))/g, ".")}</h5>
                )}
            </>
        )
    );

    const addToCartQuick = (event, item) => {
        const itemOptions = getItemOptions(item || {});
        if (itemOptions.length > 1) {
            navigate(getProductPath(item));
            return;
        }

        event.preventDefault();
        event.stopPropagation();

        const selectedItem = itemOptions[0]?.label || '';
        const availableStock = itemOptions.length > 0
            ? Math.max(0, Number(itemOptions[0]?.stock) || 0)
            : Math.max(0, Number(item?.stock) || 0);

        if (availableStock < 1) {
            return;
        }

        const cart = getStoredCartItems();
        const existingItemIndex = cart.findIndex((cartItem) =>
            cartItem.idProducto === item.idProducto && cartItem.item === selectedItem
        );
        const quantityInCart = existingItemIndex !== -1 ? cart[existingItemIndex].cantidad : 0;

        if (quantityInCart + 1 > availableStock) {
            return;
        }

        if (existingItemIndex !== -1) {
            cart[existingItemIndex] = {
                ...cart[existingItemIndex],
                cantidad: quantityInCart + 1,
            };
        } else {
            cart.push({ idProducto: item.idProducto, item: selectedItem, cantidad: 1 });
        }

        localStorage.setItem('cart', JSON.stringify(serializeCartItems(cart)));
        window.dispatchEvent(new Event('cart-updated'));
    };

    const handleSearchInputChange = (event) => {
        setSearchText(event.target.value);
    };

    const handlePrecioChange = (event) => {
        const precio = parseFloat(event.target.value);
        setPrecioActual(precio);
    };

    const handleCategoriaSelect = (event) => {
        setCategoriasSeleccionadas(event.target.value);
    };

    const handleMarcaSelect = (event) => {
        setMarcaSeleccionada(event.target.value);
    };

    const marcasDisponibles = [...new Set(
        productos
            .map((producto) => String(producto?.marca || '').trim())
            .filter(Boolean)
    )].sort((left, right) => left.localeCompare(right, 'es', { sensitivity: 'base' }));

    const filterProductos = (producto) => {
        console.log('Filtrando producto:', producto); // Debug
        const categoriaMatch = categoriasSeleccionadas === '' || producto.idCategoria.toString().includes(categoriasSeleccionadas);
        const marcaMatch = marcaSeleccionada === '' || String(producto?.marca || '') === marcaSeleccionada;
        const searchTextMatch = searchText === '' || producto.titulo.toLowerCase().includes(searchText.toLowerCase());
        const precioMatch = isCustomPrice(producto) || (producto.precio >= precioMinimo && producto.precio <= precioActual);
        return categoriaMatch && marcaMatch && searchTextMatch && precioMatch;
    };

    return (
        <div className='ProductsContainPage'>
            <Cart />

            {productos?.length > 0 && (
                <div className='filtrosPage'>
                    <div className='searchInput'>
                        {productos?.length > 0 &&
                            <fieldset className="inputSearch">
                                <FontAwesomeIcon icon={faSearch} className="search-icon" />
                                <input
                                    type="text"
                                    placeholder="Buscar..."
                                    value={searchText}
                                    onChange={handleSearchInputChange}
                                    className='input'
                                />
                            </fieldset>
                        }
                    </div>
                    <div className='inputsGrap'>
                        <select
                            value={categoriasSeleccionadas}
                            onChange={handleCategoriaSelect}
                        >
                            <option value="">Categorías...</option>
                            {categorias.map(categoria => (
                                <option key={categoria.idCategoria} value={categoria.idCategoria}>
                                    {categoria.categoria}
                                </option>
                            ))}
                        </select>

                        <select
                            value={marcaSeleccionada}
                            onChange={handleMarcaSelect}
                        >
                            <option value="">Marcas...</option>
                            {marcasDisponibles.map((marca) => (
                                <option key={marca} value={marca}>{marca}</option>
                            ))}
                        </select>
                    </div>

                    <div className='range'>
                        <input
                            type="range"
                            min={precioMinimo}
                            max={precioMaximo}
                            value={precioActual}
                            onChange={handlePrecioChange}
                        />
                        <div>{getProductMoneda()} {precioActual.toLocaleString()}</div>
                    </div>
                </div>
            )}
            <div>
                {loading ? (
                    <ProductosLoading />
                ) : (
                    <div className='ProductsGrap'>
                        {productos
                            .filter(filterProductos)
                            .map(item => {
                                const isOutOfStock = getAvailableStock(item) < 1;

                                return (
                                    <Anchor className='cardProdcutmasVendido' key={item.idProducto} to={getProductPath(item)}>
                                        <ProductMedia src={obtenerImagen(item)} alt={item.titulo} className="productMediaCard" autoPlay muted loop />
                                        <div className='cardText'>
                                            <h4>{item.titulo}</h4>
                                            <div className='cardMetaText'>
                                                {item.marca && <span>{item.marca}</span>}
                                                {getCategoryLabel(item.idCategoria) && <span>{getCategoryLabel(item.idCategoria)}</span>}
                                            </div>
                                            <div className='cardFooterRow'>
                                                <div className='deFLexPrice'>
                                                    {renderPrice(item)}
                                                </div>
                                                <div className='cardActionStack'>
                                                    {isOutOfStock && <span className='outOfStockLabel'>{outOfStockLabel}</span>}
                                                    <button
                                                        type='button'
                                                        className='quickAddButton'
                                                        onClick={(event) => addToCartQuick(event, item)}
                                                        aria-label={`Agregar ${item.titulo} al carrito`}
                                                    >
                                                        <FontAwesomeIcon icon={faShoppingCart} />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </Anchor>
                                );
                            })}
                    </div>
                )}
            </div>
        </div>
    );
}
